import { resolveFieldValue } from '@citizen-walk/shared-types';
import {
  filterPostsByCategory,
  filterPostsByDateRange,
  filterPostsBySession,
  type PostMinimal,
} from './usePosts';

/**
 * 一覧 / 地図 filter の合成を pure に評価する helper。
 *
 * Phase F (old2 「メイン地図 / 投稿一覧 / フィルタ」逐語移植) で old2
 * `apps/user/src/contexts/FilterContext.tsx` の applyFilters 相当へ全面拡張。
 *
 * 旧 (user 第 9 弾) は session / date / category の 3 段のみだったが、本拡張で old2 と同じ
 * 多段フィルタ (keyword / sessionIds[] / minAgreeCount / hasPhoto / onlyMine / postFieldFilters /
 * userFieldFilters) を合成できるようにした。AppHome / MapPage が hoist する filter state を
 * そのまま受け取り、active 判定 + 適用 + 件数表示用フラグを 1 回の呼出で返す。
 *
 * 後方互換: 旧 3 段 (filterBySession / currentSessionId / dateFrom / dateTo /
 * selectedCategoryCodes) は維持し、追加 field は全て optional (省略時は old 挙動と同一)。
 *
 * old2 との差分:
 *   - `source` (gps/manual) フィルタ: old2 では FilterState に存在するが UI からは未露出のため未実装。
 *   - `userFieldFilters` の参照先: old2 は participants/{authorUid} の profile を別途ロードして join する
 *     (useAuthorProfiles)。v2 は cross-author profile ローダーが未提供のため、post 自身の extraFields を
 *     `resolveFieldValue` で参照する (post に投稿者属性が入っているケースのみヒット)。
 *   - AI 解析軸フィルタ (M3 outputFormat classificationAxes / assessment) は v2 に loader が無く未移植。
 *     AI カテゴリ (M2) は `selectedCategoryCodes` (filterPostsByCategory) でカバー。
 *
 * 入力 state は localStorage 永続化されない (画面遷移で消える) 設計のため、Page 側で useState 管理。
 * dateFrom/dateTo は yyyy-mm-dd の文字列 (input[type=date] の value 形式)、空文字は filter 無効。
 */
export interface PostFilterState {
  filterBySession: boolean;
  currentSessionId: string | null;
  dateFrom: string;
  dateTo: string;
  selectedCategoryCodes: string[];
  /** old2: タイトル / コメント / 投稿者名 の部分一致 (大文字小文字無視)。空文字は無効。 */
  keyword?: string;
  /** old2: セッション chip 複数選択 (OR)。空配列は無効。filterBySession とは独立。 */
  sessionIds?: string[];
  /** old2: 最小いいね数 (agreeCount >= n)。0 は無効。 */
  minAgreeCount?: number;
  /** old2: 写真ありのみ (true) / なしのみ (false) / 無効 (null)。 */
  hasPhoto?: boolean | null;
  /** old2: 自分の投稿のみ表示。currentUid / selfUids と組で評価。 */
  onlyMine?: boolean;
  /** old2: 現在の認証 uid (onlyMine 評価用)。 */
  currentUid?: string | null;
  /**
   * R-70 方針B: onlyMine で「自分」とみなす全 uid (現 uid + 端末引き継ぎ元 linkedUids)。
   * 指定時はこちらを優先 (引き継ぎ後も旧端末の投稿を自分のものとして表示)。省略時は currentUid 単独。
   */
  selfUids?: string[];
  /** old2: 投稿項目フィルタ {fieldKey: 選択値[]}。各 key は AND、値配列は OR。 */
  postFieldFilters?: Record<string, string[]>;
  /** old2: 投稿者属性フィルタ {fieldKey: 選択値[]}。post extraFields を参照 (上記差分参照)。 */
  userFieldFilters?: Record<string, string[]>;
}

export interface PostFilterResult {
  filtered: PostMinimal[];
  sessionActive: boolean;
  dateActive: boolean;
  categoryActive: boolean;
  filterActive: boolean;
  /** old2 FilterButton の赤丸 badge 用 active filter 段数。 */
  activeFilterCount: number;
}

/**
 * 値とフィルタ選択肢のマッチング判定 (old2 FilterContext.matchesValue と同一)。
 * - 配列値 (multiselect): いずれか一致で OK
 * - 単一値: 一致で OK
 * - null/undefined: 不一致
 */
function matchesValue(raw: unknown, selectedVals: string[]): boolean {
  if (raw == null) return false;
  if (Array.isArray(raw)) {
    return raw.some((v) => selectedVals.includes(String(v)));
  }
  return selectedVals.includes(String(raw));
}

function entriesWithValues(
  filters: Record<string, string[]> | undefined,
): Array<[string, string[]]> {
  return Object.entries(filters ?? {}).filter(([, vals]) => vals.length > 0);
}

export function applyPostFilters(posts: PostMinimal[], state: PostFilterState): PostFilterResult {
  const sessionActive = state.filterBySession && state.currentSessionId !== null;
  const fromDate = state.dateFrom ? new Date(state.dateFrom) : null;
  const toDate = state.dateTo ? new Date(state.dateTo) : null;
  const dateActive = fromDate !== null || toDate !== null;
  const categoryActive = state.selectedCategoryCodes.length > 0;

  let count = 0;
  let result = posts;

  // ─── keyword (old2: title / comment / authorNickname の部分一致) ───
  const keyword = state.keyword?.trim() ?? '';
  if (keyword) {
    count++;
    const kw = keyword.toLowerCase();
    result = result.filter(
      (p) =>
        p.title.toLowerCase().includes(kw) ||
        (p.comment?.toLowerCase().includes(kw) ?? false) ||
        p.authorNickname.toLowerCase().includes(kw),
    );
  }

  // ─── 現セッションのみ (filterBySession + currentSessionId、旧 user 第 3 弾) ───
  if (sessionActive) {
    count++;
    result = filterPostsBySession(result, state.currentSessionId);
  }

  // ─── セッション chip 複数選択 (old2 sessionIds[]、OR、未紐付け post は除外) ───
  const sessionIds = state.sessionIds ?? [];
  if (sessionIds.length > 0) {
    count++;
    const set = new Set(sessionIds);
    result = result.filter((p) => p.sessionId !== null && set.has(p.sessionId));
  }

  // ─── 日付範囲 (旧 user 第 4 弾) ───
  if (dateActive) {
    count++;
    result = filterPostsByDateRange(result, fromDate, toDate);
  }

  // ─── AI カテゴリ (M2、旧 user 第 5/10 弾) ───
  if (categoryActive) {
    count++;
    result = filterPostsByCategory(result, state.selectedCategoryCodes);
  }

  // ─── 最小いいね数 (old2 minAgreeCount) ───
  const minAgree = state.minAgreeCount ?? 0;
  if (minAgree > 0) {
    count++;
    result = result.filter((p) => p.agreeCount >= minAgree);
  }

  // ─── 写真あり / なし (old2 hasPhoto) ───
  if (state.hasPhoto != null) {
    count++;
    const want = state.hasPhoto;
    result = result.filter((p) => (want ? !!p.photoPath : !p.photoPath));
  }

  // ─── 自分の投稿のみ (old2 onlyMine) ───
  // R-70: selfUids (現 uid + 引き継ぎ元) があればそれで判定、無ければ currentUid 単独 (後方互換)。
  if (state.onlyMine) {
    const selfUids =
      state.selfUids && state.selfUids.length > 0
        ? state.selfUids
        : state.currentUid
          ? [state.currentUid]
          : [];
    if (selfUids.length > 0) {
      count++;
      const set = new Set(selfUids);
      result = result.filter((p) => set.has(p.authorUid));
    }
  }

  // ─── 投稿項目フィルタ (old2 postFieldFilters、resolveFieldValue 参照) ───
  const postEntries = entriesWithValues(state.postFieldFilters);
  if (postEntries.length > 0) {
    result = result.filter((p) => {
      for (const [key, selectedVals] of postEntries) {
        const raw = resolveFieldValue(p as unknown as Record<string, unknown>, key);
        if (!matchesValue(raw, selectedVals)) return false;
      }
      return true;
    });
    count += postEntries.length;
  }

  // ─── 投稿者属性フィルタ (old2 userFieldFilters、v2 は post extraFields を参照) ───
  const userEntries = entriesWithValues(state.userFieldFilters);
  if (userEntries.length > 0) {
    result = result.filter((p) => {
      for (const [key, selectedVals] of userEntries) {
        const raw = resolveFieldValue(p as unknown as Record<string, unknown>, key);
        if (!matchesValue(raw, selectedVals)) return false;
      }
      return true;
    });
    count += userEntries.length;
  }

  return {
    filtered: result,
    sessionActive,
    dateActive,
    categoryActive,
    filterActive: count > 0,
    activeFilterCount: count,
  };
}
